// src/scenes/DebugScene.ts
import Phaser from 'phaser';
import { SceneKey } from './sceneKeys';
import { NORMAL_MONSTER_IDS } from '../constants/monsters';
import { loadGameProgress, saveGameProgress, PROGRESS_STORAGE_KEY } from '../utils/gameProgressManager';
import { isDebugBossWeakened, setDebugBossWeakened, clearDebugFlags } from '../utils/debugManager';
import { AchievementId } from '../types/Achievement';
import { unlockAchievements, ACHIEVEMENT_STORAGE_KEY } from '../utils/achievementManager';
import { SURVIVAL_STORAGE_KEY, SURVIVAL_BEST_KEY } from '../utils/survivalManager';
import { TournamentState, TournamentMode, TournamentPhase } from '../types/Tournament';
import { AILevel } from '../ai/types';
import { BattleResultType } from '../types/BattleState';
import { createParticipants } from '../tournament/participantFactory';
import { generateEliminationBracket } from '../tournament/eliminationBracketGenerator';
import { recordMatchResult, advanceEliminationBracket } from '../tournament/tournamentManager';

const DEFAULT_PROGRESS = {
  clearedStages: 0,
  bossDefeatedWith: [] as string[],
  unlockedHiddenMonsters: [] as string[],
  tournamentWonAtShukuren: false,
  anyTournamentWon: false,
};

const SURVIVAL_DEBUG_DATA = {
  monsterId: 'zaag',
  winCount: 100,
  bonusPoints: 300,
  stats: { hp: 999, strength: 500, special: 400, speed: 450, toughness: 380 },
  opponentQueue: ['gardan', 'roona', 'zephyr', 'balga', 'morsu', 'graon', 'igna'],
};

export class DebugScene extends Phaser.Scene {
  private scrollContainer!: Phaser.GameObjects.Container;
  private containerOffsetY = 0;
  private totalContentHeight = 0;

  constructor() {
    super({ key: SceneKey.DEBUG });
  }

  create(): void {
    const gw = this.cameras.main.width;
    const gh = this.cameras.main.height;
    const HEADER_H = 50;
    const FOOTER_H = 50;

    // 背景
    this.add.rectangle(gw / 2, gh / 2, gw, gh, 0x111122);

    // ヘッダー（固定）
    this.add.text(gw / 2, 16, '🔧 デバッグメニュー', {
      fontSize: '18px', color: '#ff9944', fontFamily: 'sans-serif',
    }).setOrigin(0.5, 0).setDepth(10);

    // フッター「戻る」ボタン（固定）
    const backBg = this.add.rectangle(gw / 2, gh - FOOTER_H / 2, gw - 40, 36, 0x334455)
      .setInteractive({ useHandCursor: true }).setDepth(10);
    this.add.text(gw / 2, gh - FOOTER_H / 2, 'タイトルに戻る', {
      fontSize: '15px', color: '#ffffff', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(11);
    backBg.on('pointerdown', () => this.scene.start(SceneKey.TITLE));

    // スクロール可能コンテナ
    this.scrollContainer = this.add.container(0, HEADER_H);
    this.buildContent(gw);
    this.setupScroll(gw, gh, HEADER_H, FOOTER_H);
  }

  private buildContent(gw: number): void {
    let y = 8;
    const cx = gw / 2;

    const addCategory = (label: string): void => {
      const txt = this.add.text(cx, y, label, {
        fontSize: '12px', color: '#888888', fontFamily: 'sans-serif',
      }).setOrigin(0.5, 0);
      this.scrollContainer.add(txt);
      y += 22;
    };

    const addButton = (label: string, fillColor: number, onClick: () => void): void => {
      const bg = this.add.rectangle(cx, y + 16, gw - 32, 30, fillColor)
        .setInteractive({ useHandCursor: true });
      const txt = this.add.text(cx, y + 16, label, {
        fontSize: '13px', color: '#ffffff', fontFamily: 'sans-serif',
      }).setOrigin(0.5);
      this.scrollContainer.add([bg, txt]);
      bg.on('pointerover', () => bg.setAlpha(0.75));
      bg.on('pointerout', () => bg.setAlpha(1));
      bg.on('pointerdown', () => onClick());
      y += 38;
    };

    const addResetButton = (label: string, confirmMsg: string, onClick: () => void): void => {
      addButton(label, 0x552222, () => this.showConfirmDialog(confirmMsg, onClick));
    };

    // ── 進捗 ──
    addCategory('── 進捗 ──');
    addButton('7ステージクリア済み', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, clearedStages: 7 });
    });
    addButton('8ステージクリア済み', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, clearedStages: 8 });
    });
    addButton('9ステージ(ボス)クリア済み', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, clearedStages: 9 });
    });
    addResetButton('進捗リセット', '進捗をリセットしますか？', () =>
      saveGameProgress({ ...DEFAULT_PROGRESS })
    );

    // ── 隠しキャラ ──
    addCategory('── 隠しキャラ ──');
    addButton('アリス解放条件セット', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, clearedStages: Math.max(p.clearedStages, 9), tournamentWonAtShukuren: true });
    });
    addButton('モコモコ解放条件セット', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, bossDefeatedWith: [...NORMAL_MONSTER_IDS] });
    });
    addButton('アリス解放', 0x2a4a2a, () => {
      const p = loadGameProgress();
      if (!p.unlockedHiddenMonsters.includes('alice')) {
        saveGameProgress({ ...p, unlockedHiddenMonsters: [...p.unlockedHiddenMonsters, 'alice'] });
      }
    });
    addButton('モコモコ解放', 0x2a4a2a, () => {
      const p = loadGameProgress();
      if (!p.unlockedHiddenMonsters.includes('mococo')) {
        saveGameProgress({ ...p, unlockedHiddenMonsters: [...p.unlockedHiddenMonsters, 'mococo'] });
      }
    });
    addResetButton('隠しキャラ解放状態リセット', '隠しキャラ解放状態をリセットしますか？', () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, unlockedHiddenMonsters: [] });
    });

    // ── ボス ──
    addCategory('── ボス ──');
    addButton('ボス全員撃破済み', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, bossDefeatedWith: [...NORMAL_MONSTER_IDS] });
    });
    addButton('ボス7キャラ撃破済み（レイン残し）', 0x2a3a4a, () => {
      const p = loadGameProgress();
      saveGameProgress({ ...p, bossDefeatedWith: NORMAL_MONSTER_IDS.filter(id => id !== 'zaag') });
    });

    // ボス弱体化トグルボタン（状態表示あり）
    const weakenBg = this.add.rectangle(cx, y + 16, gw - 32, 30, this.weakenColor())
      .setInteractive({ useHandCursor: true });
    const weakenTxt = this.add.text(cx, y + 16, this.weakenLabel(), {
      fontSize: '13px', color: '#ffffff', fontFamily: 'sans-serif',
    }).setOrigin(0.5);
    this.scrollContainer.add([weakenBg, weakenTxt]);
    weakenBg.on('pointerdown', () => {
      setDebugBossWeakened(!isDebugBossWeakened());
      weakenBg.setFillStyle(this.weakenColor());
      weakenTxt.setText(this.weakenLabel());
    });
    y += 38;

    addResetButton('強化リセット（弱体化OFF）', '強化設定をリセットしますか？', () => clearDebugFlags());

    // ── 大会 ──
    addCategory('── 大会 ──');
    addButton('大会即優勝（熟練/zaag）', 0x2a4a2a, () => {
      const state = buildDebugTournamentWinState(AILevel.LV5);
      this.scene.start(SceneKey.TOURNAMENT_RESULT, { tournamentState: state });
    });
    addButton('大会即優勝（達人/zaag）', 0x2a4a2a, () => {
      const state = buildDebugTournamentWinState(AILevel.LV6);
      this.scene.start(SceneKey.TOURNAMENT_RESULT, { tournamentState: state });
    });

    // ── サバイバル ──
    addCategory('── サバイバル ──');
    addButton('100連勝状態の中断データをセット', 0x2a3a4a, () => {
      localStorage.setItem(SURVIVAL_STORAGE_KEY, JSON.stringify(SURVIVAL_DEBUG_DATA));
    });

    // ── 実績 ──
    addCategory('── 実績 ──');
    addButton('実績全解除', 0x2a4a2a, () => {
      const allIds = Object.values(AchievementId) as AchievementId[];
      unlockAchievements(allIds);
    });
    addResetButton('実績リセット', '実績をリセットしますか？', () => {
      localStorage.removeItem(ACHIEVEMENT_STORAGE_KEY);
    });

    // ── 危険 ──
    addCategory('── ⚠️ 危険 ──');
    addResetButton('全リセット（初期状態）', '全データをリセットします。\nよろしいですか？', () => {
      localStorage.removeItem(PROGRESS_STORAGE_KEY);
      localStorage.removeItem(ACHIEVEMENT_STORAGE_KEY);
      localStorage.removeItem(SURVIVAL_STORAGE_KEY);
      localStorage.removeItem(SURVIVAL_BEST_KEY);
      clearDebugFlags();
    });

    y += 20;
    this.totalContentHeight = y;
  }

  private weakenColor(): number {
    return isDebugBossWeakened() ? 0x664400 : 0x2a3a4a;
  }

  private weakenLabel(): string {
    return isDebugBossWeakened() ? 'ボス弱体化 [ON]' : 'ボス弱体化 [OFF]';
  }

  private setupScroll(gw: number, gh: number, headerH: number, footerH: number): void {
    const visibleH = gh - headerH - footerH;
    let startY = 0;
    let isScrolling = false;

    this.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      startY = p.y - this.containerOffsetY;
      isScrolling = false;
    });
    this.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!p.isDown) return;
      if (Math.abs(p.y - (startY + this.containerOffsetY)) > 6) isScrolling = true;
      if (!isScrolling) return;
      const minY = Math.min(0, -(this.totalContentHeight - visibleH));
      this.containerOffsetY = Phaser.Math.Clamp(p.y - startY, minY, 0);
      this.scrollContainer.setY(headerH + this.containerOffsetY);
    });
    this.input.on('pointerup', () => { isScrolling = false; });
    this.input.on('pointerupoutside', () => { isScrolling = false; });
  }

  private showConfirmDialog(message: string, onConfirm: () => void): void {
    const gw = this.cameras.main.width;
    const gh = this.cameras.main.height;
    const cx = gw / 2;
    const cy = gh / 2;
    const DEPTH = 50;

    const overlay = this.add.rectangle(cx, cy, gw, gh, 0x000000, 0.72)
      .setDepth(DEPTH).setInteractive();
    const panel = this.add.rectangle(cx, cy, Math.min(gw - 40, 320), 170, 0x1e1e2e)
      .setDepth(DEPTH + 1);
    const txt = this.add.text(cx, cy - 38, message, {
      fontSize: '14px', color: '#ffffff', fontFamily: 'sans-serif',
      align: 'center', wordWrap: { width: Math.min(gw - 80, 280) },
    }).setOrigin(0.5).setDepth(DEPTH + 2);

    const okBg = this.add.rectangle(cx - 68, cy + 48, 116, 36, 0xaa2222)
      .setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
    const okTxt = this.add.text(cx - 68, cy + 48, 'OK', {
      fontSize: '15px', color: '#ffffff', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(DEPTH + 3);

    const cancelBg = this.add.rectangle(cx + 68, cy + 48, 116, 36, 0x445566)
      .setDepth(DEPTH + 2).setInteractive({ useHandCursor: true });
    const cancelTxt = this.add.text(cx + 68, cy + 48, 'キャンセル', {
      fontSize: '15px', color: '#ffffff', fontFamily: 'sans-serif',
    }).setOrigin(0.5).setDepth(DEPTH + 3);

    const all = [overlay, panel, txt, okBg, okTxt, cancelBg, cancelTxt];
    const close = (): void => all.forEach(o => o.destroy());

    okBg.on('pointerdown', () => { close(); onConfirm(); });
    cancelBg.on('pointerdown', () => close());
    overlay.on('pointerdown', () => close());
  }
}

/**
 * デバッグ用: プレイヤー（zaag）が優勝した大会状態を生成する。
 * 全7試合を確定結果で埋め、TournamentResultSceneへ渡す用途。
 */
function buildDebugTournamentWinState(aiLevel: AILevel): TournamentState {
  const { unlockedHiddenMonsters } = loadGameProgress();
  const playerMonsterId = 'zaag';
  const participants = createParticipants(playerMonsterId, undefined, unlockedHiddenMonsters);
  const participantIds = participants.map((p) => p.id);
  const matches = generateEliminationBracket(participantIds);

  let state: TournamentState = {
    mode: TournamentMode.SINGLE_ELIMINATION,
    participants,
    matches,
    currentMatchIndex: 0,
    phase: TournamentPhase.NOT_STARTED,
    aiLevel,
  };

  // QF: player wins match0
  state = recordMatchResult(state, 0, { winnerId: 'player', loserId: participantIds[1], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 0);
  // QF: cpu1 wins match1
  state = recordMatchResult(state, 1, { winnerId: participantIds[2], loserId: participantIds[3], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 1);
  // QF: cpu3 wins match2
  state = recordMatchResult(state, 2, { winnerId: participantIds[4], loserId: participantIds[5], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 2);
  // QF: cpu5 wins match3
  state = recordMatchResult(state, 3, { winnerId: participantIds[6], loserId: participantIds[7], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 3);
  // SF: player wins match4
  state = recordMatchResult(state, 4, { winnerId: 'player', loserId: participantIds[2], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 4);
  // SF: cpu3 wins match5
  state = recordMatchResult(state, 5, { winnerId: participantIds[4], loserId: participantIds[6], battleResult: BattleResultType.PLAYER1_WIN });
  state = advanceEliminationBracket(state, 5);
  // Final: player wins match6
  state = recordMatchResult(state, 6, { winnerId: 'player', loserId: participantIds[4], battleResult: BattleResultType.PLAYER1_WIN });

  return state;
}
